import { ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  shareVisibilityForRole,
  type ManagementSharingAction,
  type ManagementGrant,
} from '@qkindex/contracts';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, Theme } from '@mui/material/styles';
import {
  manageSharing,
  ShareTag,
  SharingResult,
  sharedPath,
} from './sharingService';
import { useFeedback } from '../feedback/FeedbackProvider';
import { CreateTagDialog } from './CreateTagDialog';
import { TagPill } from './TagPill';
import { DialogHeader } from '../../components/ui/DialogHeader';
import { DialogContent } from '../../components/ui/DialogContent';
import { DialogActions } from '../../components/ui/DialogActions';
import { LoadingState, SectionEmptyState } from '../../components/ui/States';
import {
  AddIcon,
  ChevronRightIcon,
  LinkIcon,
  MoreIcon,
  ShareIcon,
  SyncIcon,
  TagIcon,
  UserPlusIcon,
} from '../../components/ui/icons';

const roleNames: Record<string, string> = {
  viewer: 'Viewer',
  editor: 'Editor',
  'editor-transfers': 'Editor + transfers',
};
type AccessEditor = {
  kind: ManagementGrant['kind'];
  grant?: ManagementGrant;
  email: string;
  role: ManagementGrant['role'];
  location: boolean;
  quantity: boolean;
};
const headingSx = { fontSize: 20, fontWeight: 500, letterSpacing: '-0.02em' };
const rowSx = { py: 2.375, borderBottom: 1, borderColor: 'divider' };
const actionSx = { px: 0, minWidth: 0, fontSize: 14, fontWeight: 500 };
const badgeSx = {
  borderRadius: '8px',
  height: 'auto',
  minHeight: 26,
  fontSize: 12,
  '&.MuiChip-filled.MuiChip-colorDefault': {
    bgcolor: (theme: Theme) => alpha(theme.palette.primary.main, 0.08),
    color: 'primary.main',
  },
  '& .MuiChip-label': { px: 1.125, py: 0.5 },
};
function AccessRow({
  identity,
  status,
  grant,
  busy,
  onSettings,
  credential,
  children,
}: {
  identity: string;
  status: string;
  grant: ManagementGrant;
  busy: boolean;
  onSettings: () => void;
  credential?: { url: string; copied: boolean };
  children?: ReactNode;
}) {
  const settings = (
    <Button
      sx={{
        ...actionSx,
        ...(grant.kind === 'person' ? { alignSelf: 'flex-end' } : {}),
      }}
      endIcon={<ChevronRightIcon />}
      disabled={busy}
      onClick={onSettings}
    >
      Settings
    </Button>
  );
  return (
    <Stack spacing={0.5} sx={rowSx}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          flexWrap: 'wrap',
        }}
      >
        <Typography
          sx={{
            fontSize: 15,
            fontWeight: 500,
            overflowWrap: 'anywhere',
            minWidth: 0,
          }}
        >
          {identity}
        </Typography>
        <Chip sx={badgeSx} size="small" label={roleNames[grant.role]} />
      </Box>
      <Typography sx={{ fontSize: 13 }} color="text.secondary">
        {status}
      </Typography>
      <Typography sx={{ fontSize: 13 }} color="text.secondary">
        Location{' '}
        {shareVisibilityForRole(grant.role, grant.visibility).location
          ? 'visible'
          : 'hidden'}{' '}
        · Quantity{' '}
        {shareVisibilityForRole(grant.role, grant.visibility).quantity
          ? 'visible'
          : 'hidden'}
      </Typography>
      {grant.kind === 'link' ? (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
          {children}
          {settings}
        </Box>
      ) : (
        settings
      )}
      {credential && (
        <>
          <CredentialField value={credential.url} />
          {credential.copied && (
            <Typography role="status" variant="body2">
              Link copied
            </Typography>
          )}
        </>
      )}
    </Stack>
  );
}
function CredentialField({ value }: { value: string }) {
  return (
    <TextField
      label="Copy this link"
      value={value}
      slotProps={{ input: { readOnly: true } }}
      onFocus={(event) => event.target.select()}
    />
  );
}
export function CollectionSharingSection({
  collectionId,
  onClose,
  onBack,
  onPendingChange,
}: {
  collectionId: string;
  userId: string;
  onClose?: () => void;
  onBack?: () => void;
  onPendingChange?: (pending: boolean) => void;
}) {
  const { confirm } = useFeedback();
  const [data, setData] = useState<SharingResult>({});
  const [tag, setTag] = useState<ShareTag | null>(null);
  const [creatingTag, setCreatingTag] = useState(false);
  const [creatingPending, setCreatingPending] = useState(false);
  const [rename, setRename] = useState<ShareTag | null>(null);
  const [tagName, setTagName] = useState('');
  const [editor, setEditor] = useState<AccessEditor | null>(null);
  const [generatedLink, setGeneratedLink] = useState('');
  const [copiedLink, setCopiedLink] = useState<{
    shareId: string;
    url: string;
    copied: boolean;
  }>();
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    onPendingChange?.(busy || creatingPending);
    return () => onPendingChange?.(false);
  }, [busy, creatingPending, onPendingChange]);
  const [tagMenu, setTagMenu] = useState<{
    anchor: HTMLElement;
    tag: ShareTag;
  } | null>(null);
  useEffect(() => {
    let active = true;
    setLoaded(false);
    setError('');
    setData({});
    setTag(null);
    setCreatingTag(false);
    setEditor(null);
    setTagMenu(null);
    setGeneratedLink('');
    setCopiedLink(undefined);
    void manageSharing(collectionId, 'list')
      .then((result) => {
        if (active) {
          setData(result);
          setLoaded(true);
        }
      })
      .catch(() => {
        if (active) setError('Unable to load sharing settings.');
      });
    return () => {
      active = false;
    };
  }, [collectionId]);
  const run = async (
    action: ManagementSharingAction,
    fields: Record<string, unknown>,
    after?: (result: SharingResult) => void,
  ) => {
    setBusy(true);
    setError('');
    try {
      const result = await manageSharing(collectionId, action, fields);
      after?.(result);
      setData(await manageSharing(collectionId, 'list'));
    } catch (failure) {
      setError(
        failure instanceof Error
          ? failure.message
          : 'Unable to save sharing settings.',
      );
    } finally {
      setBusy(false);
    }
  };
  const resetAccess = () => {
    setEditor(null);
    setGeneratedLink('');
    setCopiedLink(undefined);
    setError('');
  };
  const openEditor = (kind: AccessEditor['kind'], grant?: ManagementGrant) => {
    resetAccess();
    const role = grant?.role || 'viewer';
    const visibility = shareVisibilityForRole(
      role,
      grant?.visibility || { location: true, quantity: true },
    );
    setEditor({
      kind,
      grant,
      email: grant?.kind === 'person' ? grant.email : '',
      role,
      location: visibility.location,
      quantity: visibility.quantity,
    });
  };
  const updateEditor = (fields: Partial<AccessEditor>) =>
    setEditor((current) => current && { ...current, ...fields });
  const publishLink = (result: SharingResult, invitation = false) => {
    const token = invitation ? result.invitationToken : result.linkToken;
    if (token && result.shareId)
      setGeneratedLink(
        window.location.origin +
          sharedPath(collectionId, result.shareId, token, invitation),
      );
  };
  const copyLink = async (grant: ManagementGrant) => {
    setBusy(true);
    setError('');
    setCopiedLink(undefined);
    try {
      const result = await manageSharing(collectionId, 'copyLink', {
        shareId: grant.shareId,
      });
      if (!result.linkToken)
        throw new Error('Unable to retrieve this sharing link.');
      const url =
        window.location.origin +
        sharedPath(collectionId, grant.shareId, result.linkToken);
      let copied = false;
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(url);
          copied = true;
        }
      } catch {
        /* Keep the selectable link available when clipboard access is blocked. */
      }
      setCopiedLink({ shareId: grant.shareId, url, copied });
    } catch (failure) {
      setError(
        failure instanceof Error
          ? failure.message
          : 'Unable to copy sharing link.',
      );
    } finally {
      setBusy(false);
    }
  };
  const tagGrants = new Map<
    string,
    {
      links: ManagementGrant[];
      people: Extract<ManagementGrant, { kind: 'person' }>[];
    }
  >();
  for (const grant of data.grants || []) {
    if (grant.state !== 'active') continue;
    const group = tagGrants.get(grant.tagId) || { links: [], people: [] };
    if (grant.kind === 'link') group.links.push(grant);
    else group.people.push(grant);
    tagGrants.set(grant.tagId, group);
  }
  const { links, people } = tagGrants.get(tag?.tagId || '') || {
    links: [],
    people: [],
  };
  const tags = (data.tags || []).filter((value) => value.state === 'active');
  const closeTag = () => {
    if (!busy) {
      setTag(null);
      resetAccess();
    }
  };
  const backToSharing = () => {
    if (!busy) resetAccess();
  };
  const listError = error && <Alert severity="error">{error}</Alert>;
  const contentRef = useRef<HTMLDivElement>(null);
  const scrollPositions = useRef(new Map<string, number>());
  const viewKey = creatingTag
    ? 'create'
    : rename
      ? 'rename'
      : tag
        ? generatedLink
          ? 'credential'
          : editor
            ? 'access'
            : `sharing:${tag.tagId}`
        : 'tags';
  useLayoutEffect(() => {
    const content = contentRef.current;
    const scroll = content?.querySelector('.MuiDialogContent-root');
    if (scroll) scroll.scrollTop = scrollPositions.current.get(viewKey) || 0;
    const input =
      creatingTag || rename ? content?.querySelector('input') : null;
    (input || content)?.focus({ preventScroll: true });
  }, [viewKey, creatingTag, rename]);
  const frame = (children: ReactNode) => (
    <Box
      ref={contentRef}
      tabIndex={-1}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        flex: 1,
        outline: 'none',
      }}
      onScrollCapture={(event) => {
        const target = event.target as HTMLElement;
        if (target.classList.contains('MuiDialogContent-root'))
          scrollPositions.current.set(viewKey, target.scrollTop);
      }}
    >
      {children}
    </Box>
  );

  if (creatingTag)
    return frame(
      <CreateTagDialog
        key={collectionId}
        collectionId={collectionId}
        tags={data.tags || []}
        onPendingChange={setCreatingPending}
        onBack={() => setCreatingTag(false)}
        onClose={onClose || (() => setCreatingTag(false))}
        onCreated={(created) => {
          setData((current) => ({
            ...current,
            tags: [
              { ...created, schemaVersion: 1, collectionId },
              ...(current.tags || []).filter(
                (value) => value.tagId !== created.tagId,
              ),
            ],
          }));
          setCreatingTag(false);
        }}
      />,
    );
  if (rename)
    return frame(
      <>
        <DialogHeader
          id="sharing-title"
          title="Rename tag"
          onBack={() => {
            if (!busy) {
              setRename(null);
              setError('');
            }
          }}
          onClose={onClose}
          closeDisabled={busy}
        />
        <DialogContent sx={{ display: 'grid', gap: 2 }}>
          {listError}
          <TextField
            autoFocus
            disabled={busy}
            label="Tag name"
            value={tagName}
            onChange={(event) => setTagName(event.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setRename(null)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={busy || !tagName.trim()}
            onClick={() => {
              if (rename)
                void run(
                  'saveTag',
                  {
                    tagId: rename.tagId,
                    expectedRevision: rename.revision,
                    name: tagName.trim(),
                  },
                  () => setRename(null),
                );
            }}
          >
            Save
          </Button>
        </DialogActions>
      </>,
    );
  if (tag)
    return frame(
      <>
        <DialogHeader
          id="sharing-title"
          title={
            <Box component="span" sx={{ display: 'grid', gap: 0.25 }}>
              <Typography
                component="span"
                sx={{ fontSize: 12, fontWeight: 400, letterSpacing: 0 }}
                color="text.secondary"
              >
                {tag.name}
              </Typography>
              <Typography component="span" sx={headingSx}>
                {generatedLink
                  ? 'Copy link'
                  : editor
                    ? editor.kind === 'link'
                      ? editor.grant
                        ? 'Link settings'
                        : 'Create share link'
                      : editor.grant
                        ? 'Person’s access'
                        : 'Invite person'
                    : 'Tag sharing'}
              </Typography>
            </Box>
          }
          onBack={editor ? backToSharing : closeTag}
          onClose={onClose}
          closeDisabled={busy}
        />
        <DialogContent
          sx={{
            px: { xs: 2.25, sm: 3.25 },
            pt: 3,
            pb: 2.5,
            overflowY: 'auto',
            '& .MuiInputBase-root': { fontSize: 15 },
            '& .MuiFormLabel-root': { fontWeight: 500 },
          }}
        >
          {listError}
          {!editor ? (
            <>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1.5,
                  flexWrap: 'wrap',
                }}
              >
                <Typography variant="h6" sx={headingSx}>
                  Share links
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  sx={{
                    fontWeight: 500,
                    borderRadius: '13px',
                    '--button-shadow': 'none',
                  }}
                  disabled={busy}
                  onClick={() => openEditor('link')}
                >
                  Create link
                </Button>
              </Box>
              {!links.length && (
                <SectionEmptyState
                  compact
                  icon={LinkIcon}
                  message="No share links yet."
                />
              )}
              {links.map((grant) => (
                <AccessRow
                  key={grant.shareId}
                  identity={roleNames[grant.role] + ' link'}
                  status={
                    grant.role === 'viewer'
                      ? 'Anyone with the link can view'
                      : 'Verified sign-in required'
                  }
                  grant={grant}
                  busy={busy}
                  onSettings={() => openEditor('link', grant)}
                  credential={
                    copiedLink?.shareId === grant.shareId
                      ? copiedLink
                      : undefined
                  }
                >
                  <Button
                    sx={actionSx}
                    startIcon={<LinkIcon />}
                    disabled={busy}
                    onClick={() => {
                      void copyLink(grant);
                    }}
                  >
                    Copy link
                  </Button>
                </AccessRow>
              ))}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1.5,
                  mt: 3,
                  flexWrap: 'wrap',
                }}
              >
                <Typography variant="h6" sx={headingSx}>
                  People with access
                </Typography>
                <Button
                  sx={actionSx}
                  startIcon={<UserPlusIcon />}
                  disabled={busy}
                  onClick={() => openEditor('person')}
                >
                  Invite person
                </Button>
              </Box>
              {!people.length && (
                <SectionEmptyState
                  compact
                  icon={UserPlusIcon}
                  message="No people invited."
                />
              )}
              {people.map((grant) => (
                <AccessRow
                  key={grant.shareId}
                  identity={grant.email}
                  status={
                    grant.invitationState === 'accepted'
                      ? 'Accepted'
                      : grant.invitationState === 'declined'
                        ? 'Declined'
                        : 'Pending invitation'
                  }
                  grant={grant}
                  busy={busy}
                  onSettings={() => openEditor('person', grant)}
                />
              ))}
            </>
          ) : generatedLink ? (
            <Stack spacing={2}>
              <CredentialField value={generatedLink} />
              <Button onClick={backToSharing}>Done</Button>
            </Stack>
          ) : (
            <Stack spacing={2.5}>
              <Typography sx={{ fontSize: 13 }} color="text.secondary">
                {editor.kind === 'link'
                  ? editor.grant
                    ? 'Link settings'
                    : 'Create share link'
                  : editor.grant
                    ? 'Person’s access'
                    : 'Invite person'}
              </Typography>
              {editor.kind === 'person' && (
                <TextField
                  label="Email"
                  type="email"
                  value={editor.email}
                  disabled={Boolean(editor.grant)}
                  onChange={(event) =>
                    updateEditor({ email: event.target.value })
                  }
                />
              )}
              <TextField
                select
                label="Access"
                value={editor.role}
                onChange={(event) =>
                  updateEditor({
                    role: event.target.value as ManagementGrant['role'],
                    ...(event.target.value !== 'viewer'
                      ? { location: true, quantity: true }
                      : {}),
                  })
                }
              >
                {Object.entries(roleNames).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </TextField>
              {editor.role === 'viewer' && (
                <Stack>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={editor.location}
                        onChange={(_, value) =>
                          updateEditor({ location: value })
                        }
                      />
                    }
                    label="Show location"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={editor.quantity}
                        onChange={(_, value) =>
                          updateEditor({ quantity: value })
                        }
                      />
                    }
                    label="Show quantity"
                  />
                </Stack>
              )}
              {editor.grant && editor.kind === 'link' && (
                <Button
                  startIcon={<SyncIcon />}
                  sx={{ ...actionSx, alignSelf: 'flex-start' }}
                  disabled={busy}
                  onClick={() => {
                    void confirm(
                      'The previous link will stop working. Anyone using it will need the replacement.',
                      'Replace link',
                    ).then((ok) => {
                      if (ok)
                        void run(
                          'rotateLink',
                          {
                            shareId: editor.grant!.shareId,
                            expectedRevision: editor.grant!.revision,
                          },
                          (result) =>
                            publishLink({
                              ...result,
                              shareId: editor.grant!.shareId,
                            }),
                        );
                    });
                  }}
                >
                  Replace link
                </Button>
              )}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  gap: 1.5,
                  flexWrap: 'wrap',
                }}
              >
                {editor.grant && (
                  <Button
                    color="error"
                    sx={{ ...actionSx, mr: 'auto' }}
                    disabled={busy}
                    onClick={() => {
                      void confirm(
                        editor.kind === 'link'
                          ? 'Disable access through this link?'
                          : 'Remove this person’s access?',
                        editor.kind === 'link'
                          ? 'Disable link'
                          : 'Remove access',
                      ).then((ok) => {
                        if (ok)
                          void run(
                            editor.kind === 'link'
                              ? 'revokeLink'
                              : 'removePerson',
                            {
                              shareId: editor.grant!.shareId,
                              expectedRevision: editor.grant!.revision,
                            },
                            resetAccess,
                          );
                      });
                    }}
                  >
                    {editor.kind === 'link' ? 'Disable link' : 'Remove access'}
                  </Button>
                )}
                <Button disabled={busy} onClick={backToSharing}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  sx={{
                    borderRadius: '13px',
                    fontWeight: 500,
                    '--button-shadow': 'none',
                  }}
                  disabled={
                    busy || (editor.kind === 'person' && !editor.email.trim())
                  }
                  onClick={() => {
                    const grant = editor.grant;
                    const fields = {
                      ...(!grant || editor.kind === 'link'
                        ? { tagId: tag!.tagId }
                        : {}),
                      ...(grant
                        ? {
                            shareId: grant.shareId,
                            expectedRevision: grant.revision,
                          }
                        : {}),
                      role: editor.role,
                      visibility: shareVisibilityForRole(editor.role, {
                        location: editor.location,
                        quantity: editor.quantity,
                      }),
                      ...(editor.kind === 'person' && !grant
                        ? { email: editor.email.trim() }
                        : {}),
                    };
                    void run(
                      editor.kind === 'link'
                        ? 'saveLink'
                        : grant
                          ? 'updatePerson'
                          : 'invite',
                      fields,
                      (result) => {
                        publishLink(result, editor.kind === 'person');
                        if (
                          grant ||
                          (!result.linkToken && !result.invitationToken)
                        )
                          setEditor(null);
                      },
                    );
                  }}
                >
                  {editor.grant
                    ? 'Save changes'
                    : editor.kind === 'link'
                      ? 'Create link'
                      : 'Create invitation'}
                </Button>
              </Box>
            </Stack>
          )}
        </DialogContent>
      </>,
    );

  return frame(
    <>
      <DialogHeader
        id="sharing-title"
        title="Tags and sharing"
        onClose={onClose}
        onBack={
          onBack
            ? () => {
                if (!busy) onBack();
              }
            : undefined
        }
        closeDisabled={busy}
      />
      <DialogContent>
        {listError}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1.5,
            mb: 1,
          }}
        >
          <Typography variant="h6" sx={headingSx}>
            Your tags
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            disabled={busy || !loaded}
            onClick={() => setCreatingTag(true)}
          >
            Create tag
          </Button>
        </Box>
        {!loaded && !error && <LoadingState label="Loading tags…" />}
        {loaded && !tags.length && (
          <SectionEmptyState icon={TagIcon} message="No tags yet." />
        )}
        {tags.map((value) => {
          const access = tagGrants.get(value.tagId);
          const linkCount = access?.links.length || 0;
          const peopleCount = access?.people.length || 0;
          const count = linkCount + peopleCount;
          const summary = [
            linkCount
              ? `${linkCount} ${linkCount === 1 ? 'link' : 'links'}`
              : '',
            peopleCount
              ? `${peopleCount} ${peopleCount === 1 ? 'person' : 'people'}`
              : '',
          ]
            .filter(Boolean)
            .join(' · ');
          return (
            <Box
              key={value.tagId}
              sx={{ ...rowSx, display: 'flex', alignItems: 'center', gap: 1.5 }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 42,
                  height: 42,
                  flexShrink: 0,
                  borderRadius: '14px',
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                  color: 'primary.main',
                  '@media (max-width: 440px)': { display: 'none' },
                }}
              >
                <TagIcon />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <TagPill
                  variant="body1"
                  tag={{ tagId: value.tagId, name: value.name }}
                />
                <Typography
                  sx={{ fontSize: 13, mt: 0.375 }}
                  color="text.secondary"
                >
                  {summary || 'Private'}
                </Typography>
              </Box>
              <Button
                disabled={busy}
                sx={{ ...actionSx, flexShrink: 0 }}
                startIcon={<ShareIcon />}
                onClick={() => {
                  setTag(value);
                  resetAccess();
                }}
              >
                {count ? 'Manage sharing' : 'Share'}
              </Button>
              <IconButton
                aria-label={`More actions for ${value.name}`}
                disabled={busy}
                sx={{ flexShrink: 0, boxShadow: 'none' }}
                onClick={(event) =>
                  setTagMenu({ anchor: event.currentTarget, tag: value })
                }
              >
                <MoreIcon />
              </IconButton>
            </Box>
          );
        })}
        <Menu
          anchorEl={tagMenu?.anchor}
          open={Boolean(tagMenu)}
          onClose={() => setTagMenu(null)}
        >
          <MenuItem
            onClick={() => {
              if (tagMenu) {
                setRename(tagMenu.tag);
                setTagName(tagMenu.tag.name);
              }
              setTagMenu(null);
            }}
          >
            Rename tag
          </MenuItem>
          <MenuItem
            onClick={() => {
              const selected = tagMenu?.tag;
              setTagMenu(null);
              if (selected)
                void confirm(
                  'Delete this tag? It will be removed from all items, and its share links and individual access will stop working. Items won’t be deleted.',
                  'Delete tag',
                ).then((ok) => {
                  if (ok) void run('deleteTag', { tagId: selected.tagId });
                });
            }}
          >
            Delete tag
          </MenuItem>
        </Menu>
      </DialogContent>
    </>,
  );
}
